import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import type { PickedContact } from './contact-references';

// Client-local shape for a contact row, matching what `/api/contacts` returns. It IS a
// PickedContact — picking a row hands the same object straight to the composer's selection.
export type ContactSummary = PickedContact;

export async function fetchContacts(search: string): Promise<ContactSummary[]> {
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  const res = await apiFetch(`${API_BASE}/api/contacts${qs}`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as ContactSummary[] | undefined;
  return Array.isArray(data) ? data : [];
}

export type ContactSearchState =
  | { status: 'loading'; contacts: ContactSummary[] }
  | { status: 'ready'; contacts: ContactSummary[] }
  | { status: 'error' };

// The search box passes an already-debounced query. Empty query → the recent-contacts default list.
export function useContactSearch(search: string): ContactSearchState {
  const query = useQuery({
    queryKey: queryKeys.contacts(search),
    queryFn: () => fetchContacts(search),
    // A recent-contacts / keyword list is rarely stale-critical; the cache keeps a debounced
    // query's prior results warm so re-opening the picker (or backspacing) doesn't refetch.
    staleTime: 60_000,
  });

  if (query.data) return { status: 'ready', contacts: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading', contacts: [] };
}
